import React, { useMemo } from "react";

import { Skeleton } from "@mui/material";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import { Contact } from "Models/contact";
import { UserCustomerPortal } from "Models/customer.portal";
import { useTranslation } from "react-i18next";
import { getOrderCustomerDisplayName } from "Util/names";

import { CustomerPortalCard } from "../../../../CustomerPortalCard";
import { CustomerPortalListItem } from "../../../../CustomerPortalListItem";
import { CustomerInfosContainer } from "../SummaryJobDetailsSection/styled";
import {
  Divider,
  Tab,
  Tabs,
  TabPanelContainer,
  InfoWrapper,
  InfoContainer,
  EmptyStateText,
  CustomerDetail,
} from "./styled";
import { SummaryUserDetailsSectionContact } from "./SummaryUserDetailsSectionContact";
import { SummaryUserDetailsSectionTabPanel } from "./SummaryUserDetailsSectionTabPanel";

const getInfoUser = (userCustomerPortal?: Partial<UserCustomerPortal>) => {
  const allInfos = [];

  if (userCustomerPortal?.email) allInfos.push(userCustomerPortal.email);
  if (userCustomerPortal?.phone) allInfos.push(userCustomerPortal.phone);
  if (userCustomerPortal?.mobile) allInfos.push(userCustomerPortal.mobile);

  if (!allInfos.length) return "-";

  return (
    <CustomerInfosContainer>
      {allInfos.map((info, index) => (
        <CustomerDetail key={index}>
          <span>{info}</span>
          <span>{index < allInfos.length - 1 && "|"}</span>
        </CustomerDetail>
      ))}
    </CustomerInfosContainer>
  );
};

export const SummaryUserDetailsSection = () => {
  const { order, isLoading } = useCustomerPortalJobContext();
  const { t: translate } = useTranslation();

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const handleChangeIndex = (index: number) => {
    setValue(index);
  };

  const tabs = useMemo(() => {
    return [
      {
        name: translate("originContact"),
        items: order?.contacts?.origin ?? [],
      },
      {
        name: translate("destinationContact"),
        items: order?.contacts?.destination ?? [],
      },
    ];
  }, [order]);

  const renderTabs = () => {
    return (
      <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
        {tabs.map((tab, index) => {
          return (
            <Tab
              label={tab.name}
              key={`tab-${tab.name}`}
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
            />
          );
        })}
      </Tabs>
    );
  };

  const renderEmptyState = () => {
    return (
      <InfoContainer>
        <EmptyStateText>{translate("noContactsAdded")}</EmptyStateText>
      </InfoContainer>
    );
  };

  const renderContacts = (items: Contact[]) => {
    if (isLoading) return;

    if (!items.length) return renderEmptyState();

    return items.map((item, index) => {
      return (
        <InfoContainer key={index}>
          <SummaryUserDetailsSectionContact item={item} isLoading={isLoading} />
        </InfoContainer>
      );
    });
  };

  const renderContactsLoading = () => {
    if (!isLoading) return;

    return Array.from({ length: 4 }).map((_item, index) => (
      <InfoContainer key={`loading-${index}`}>
        <Skeleton variant="text" sx={{ fontSize: "16px", flex: 1 }} />
      </InfoContainer>
    ));
  };

  const renderTabsPanel = () => {
    return (
      <TabPanelContainer index={value} onChangeIndex={handleChangeIndex}>
        {tabs.map((tab, index) => {
          return (
            <SummaryUserDetailsSectionTabPanel
              value={value}
              index={index}
              key={`tab-panel-${tab.name}`}
            >
              <InfoWrapper>
                {renderContactsLoading()}
                {renderContacts(tab.items)}
              </InfoWrapper>
            </SummaryUserDetailsSectionTabPanel>
          );
        })}
      </TabPanelContainer>
    );
  };

  return (
    <CustomerPortalCard title={translate("myDetails")} block isExandable>
      <CustomerPortalListItem
        title={getOrderCustomerDisplayName(order) || "-"}
        subtitle={getInfoUser(order?.customer)}
        isLoadingTitle={isLoading}
        isLoadingSubtitle={isLoading}
      />

      {renderTabs()}

      <Divider />

      {renderTabsPanel()}
    </CustomerPortalCard>
  );
};
